import React, {useCallback, useState} from 'react';
import {Alert, DatePicker, FlexColumn, Label, Modal, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../generic_modal_conductor';
import {createToast} from '../../toasts/toast';
import PortalApproverPicker from '../../pickers/PortalApproverPicker';
import {usePortalEligibleApprovers} from '../../../hooks/usePortalEligibleApprovers';
import {derivePhaseTaskBreakdown} from './phaseTaskBreakdown';
import {sendPhaseSignoffRequest} from './sendPhaseSignoffRequest';

export type SendSignoffModalPayload = {
	/** Relay global id of the phase the sign-off is asked for. */
	phaseId: string;
	/** Relay global id of the parent project — anchors the request to a client org. */
	projectId: string;
	phaseName: string;
	/** Rolled-up task totals for the phase, as the scoping table aggregates them. */
	taskCount?: number | null;
	doneCount?: number | null;
	/** The phase's configured approver (portal_users UUID), pre-selected when set. */
	approverPortalUserId?: string | null;
};

type Props = SendSignoffModalPayload & {closeModal: () => void};

/**
 * Asks a client workspace user to sign a phase off: what is being signed for, who signs
 * it, and by when.
 *
 * The task breakdown is ONE group, labelled firm-owned. Task ownership is a constant on
 * the read feed, so a client-owned group would be permanently empty; the split is a
 * separate change. The percentage is read off done/total counts rather than the phase's
 * progress, because under manual progress the progress figure is a number a PM typed and
 * can read 100% with tasks still open.
 */
export const SendSignoffModal = ({
	closeModal,
	phaseId,
	projectId,
	phaseName,
	taskCount,
	doneCount,
	approverPortalUserId,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	// One fetch for the whole modal, handed to the picker as a prop.
	const {approvers, loading, error} = usePortalEligibleApprovers(projectId);

	const [approver, setApprover] = useState<string | null>(approverPortalUserId ?? null);
	const [deadline, setDeadline] = useState<string | undefined>(undefined);
	const [submitting, setSubmitting] = useState(false);
	// A refusal the workspace explained renders inline: the shared warning modal would
	// replace this one, losing the chosen approver and deadline, and the non-critical
	// path renders behind it where it cannot be read.
	const [errorMessageId, setErrorMessageId] = useState<string | null>(null);

	const breakdown = derivePhaseTaskBreakdown({taskCount, doneCount});
	const canSend = !!approver && !submitting;

	const handleSend = useCallback(() => {
		if (!approver || submitting) return;
		setSubmitting(true);
		setErrorMessageId(null);
		sendPhaseSignoffRequest(
			{psaMilestoneId: phaseId, psaProjectId: projectId, approverPortalUserId: approver, deadline},
			() => {
				createToast({
					duration: 5000,
					variant: 'banner',
					message: (
						<Alert
							type={Alert.TYPE.SUCCESS}
							text={formatMessage({id: 'send_signoff_modal.sent_toast'}, {name: phaseName})}
						/>
					),
				});
				closeModal();
			},
			validationMessageId => {
				// Stay open so the composed request survives. An unrecognised failure has
				// already been surfaced by the shared mutation error modal.
				setSubmitting(false);
				setErrorMessageId(validationMessageId);
			}
		);
	}, [approver, submitting, phaseId, projectId, deadline, phaseName, closeModal, formatMessage]);

	return (
		<Modal onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'send_signoff_modal.title'})}</Modal.Title>
			<Modal.Content data-cy="send-signoff-modal">
				<FlexColumn gap="m">
					<Text color="medium">{formatMessage({id: 'send_signoff_modal.subtitle'}, {name: phaseName})}</Text>
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'send_signoff_modal.tasks_heading'})}</Label>
						<div data-testid="send-signoff-breakdown" data-cy="send-signoff-breakdown">
							<Text>
								{breakdown.empty
									? formatMessage({id: 'send_signoff_modal.tasks_empty'})
									: formatMessage(
											{id: 'send_signoff_modal.tasks_firm_owned'},
											{
												done: breakdown.doneCount,
												total: breakdown.taskCount,
												percent: breakdown.percent,
											}
									  )}
							</Text>
						</div>
					</FlexColumn>
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'send_signoff_modal.approver_heading'})}</Label>
						<PortalApproverPicker
							value={approver}
							approvers={approvers}
							loading={loading}
							error={error}
							onChange={setApprover}
							placeholder={formatMessage({id: 'send_signoff_modal.approver_placeholder'})}
							ariaLabel={formatMessage({id: 'send_signoff_modal.approver_heading'})}
							dataCy="send-signoff-approver"
						/>
						<div data-testid="send-signoff-eligibility-note">
							<Text color="medium">{formatMessage({id: 'send_signoff_modal.approver_note'})}</Text>
						</div>
					</FlexColumn>
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'send_signoff_modal.deadline_heading'})}</Label>
						<DatePicker
							value={deadline}
							onChange={(dateString: string) => setDeadline(dateString || undefined)}
							aria-label={formatMessage({id: 'send_signoff_modal.deadline_heading'})}
							data-cy="send-signoff-deadline"
						/>
					</FlexColumn>
					{errorMessageId && (
						<div data-testid="send-signoff-error" data-cy="send-signoff-error">
							<Alert type={Alert.TYPE.ERROR} text={formatMessage({id: errorMessageId})} />
						</div>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="send-signoff-cancel">
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
				<Modal.PrimaryFooterButton onPress={handleSend} disabled={!canSend} dataCy="send-signoff-confirm">
					{formatMessage({id: 'send_signoff_modal.send'})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const showSendSignoffModal = (payload: SendSignoffModalPayload): void => {
	showModal({type: MODAL_TYPE.SEND_SIGNOFF, ...payload});
};
